import { Button, DialogActions, DialogContent, DialogTitle, Grid, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { CustomerDto, initialOrderCyclesDto, OrderCyclesDto, RetentionSaveDto } from "./types";
import { renderClose, useDialog } from "../../common/dialog";
import { createEntityStore, UseEntityStore } from "../../common/entityStore";
import { customerOrderDialogTitle, useCustomerBasicInfo } from "./common";
import { propBoolField, propNumberField, propTextField } from "../../common/entityStoreForm";
import { createListStore } from "../../common/listStore";
import { listHeaderItems, renderListStore, useScrollToSelectedItemSubject } from "../../common/listStoreUi";
import { map } from "rxjs";
import { UserTypeId, useAccountStore } from "../../common/account";
import { OrderCycleDialog } from "./OrderCycleDialog";
import { Code, CodeOff, Edit, Start } from "@mui/icons-material";
import { createAxios } from "../../common/axios";
import { RetentionCodeId } from "../../common/enum";

export function OrderCycleListDialog(props: {
    close: () => void;
    useCustomerStore: UseEntityStore<CustomerDto>;
    ordId: number;
    orderId: number;
    retention?: RetentionSaveDto;
    clearRetention(): void;
}) {
    const customer = useCustomerBasicInfo(props.useCustomerStore);
    const [{ useOrderCyclesStore, useCyclesStore }] = useState(() => {
        const useOrderCyclesStore = createEntityStore<OrderCyclesDto>({
            path: "customers/orderCycles?orderId=" + props.orderId, data: initialOrderCyclesDto,
        });
        const useCyclesStore = createListStore({
            data: useOrderCyclesStore.getState().serverData$.pipe(map(p => ({ items: p.data.cycles, isFetching: p.isFetching }))), getId: p => p.cycleId,
            listProps: [
                { column: "cycleId", label: "Cycle", width: 80, type: "number" },
                { column: "cycQty", label: "Quantity", width: 80, type: "number" },
                { column: "skipCyc", label: "Skip cycle", width: 80, type: "bool" },
                { column: "gift", label: "Gift", width: 80, type: "bool" },
                { column: "freeShp", label: "Free shp", width: 80, type: "bool" },
                { column: "endCyc", label: "End cycle", width: 80, type: "bool" },
                { column: "startDate", label: "Start date", width: 110, type: "date" },
            ]
        });
        return { useOrderCyclesStore, useCyclesStore };
    });
    const { selectedCycle } = useCyclesStore(p => ({ selectedCycle: p.selectedItem }));
    const [useArticlesStore] = useState(() => createListStore({
        data: useCyclesStore.getState().selectedItem$.pipe(map(p => ({ items: p?.articles || [], isFetching: false }))), getId: p => p.code,
        listProps: [
            { column: "code", label: "Article", width: 80 },
            { column: "name", label: "Description", width: 350 },
        ]
    }));

    const scrollToSelectedItemSubject = useScrollToSelectedItemSubject();

    useEffect(() => {
        const sub = useCyclesStore.getState().items$.subscribe({
            next(value) {
                if (value.items.length > 0) {
                    const actualCycle = useOrderCyclesStore.getState().clientData.actualCycle;
                    const index = value.items.map((p, i) => ({ cycleId: p.cycleId, index: i })).filter(p => p.cycleId >= actualCycle).map(p => p.index).at(0);
                    if (index != null) {
                        sub.unsubscribe();
                        useCyclesStore.getState().setSelectedItem(value.items[index]);
                        scrollToSelectedItemSubject.next();
                    }
                }
            },
        });
        return () => sub.unsubscribe();
    }, [useCyclesStore, useOrderCyclesStore, scrollToSelectedItemSubject]);

    const accountStore = useAccountStore(p => ({ userTypeId: p.data?.accessTokenDecoded.userTypeId }));
    const canManage = accountStore.userTypeId !== UserTypeId.View && accountStore.userTypeId !== UserTypeId.CollectionAgency;
    const canOpenDetail = canManage;

    const orderCycleDialog = useDialog(close => <OrderCycleDialog close={close} ordId={props.ordId} orderId={props.orderId}
        useCustomerStore={props.useCustomerStore} orderCycle={selectedCycle!} useOrderCyclesStore={useOrderCyclesStore}
        retention={props.retention} clearRetention={props.clearRetention}
        onOrderCycleSaved={() => useOrderCyclesStore.getState().fetchServerData(true)} />, "sm");

    const { actualCycle } = useOrderCyclesStore(p => ({ actualCycle: p.clientData.actualCycle }));

    async function stopAutoSkip() {
        try {
            await createAxios().post("customers/orderCycles/stopAutoSkip", { orderId: props.orderId, cycleId: selectedCycle?.cycleId });
        } finally {
            useOrderCyclesStore.getState().fetchServerData(true);
        }
    }

    async function autoSkip() {
        const retention = props.retention?.retentionCodeId === RetentionCodeId.SkipEachSecondShp ? props.retention : undefined;
        try {
            await createAxios().post("customers/orderCycles/autoSkip", { orderId: props.orderId, cycleId: selectedCycle?.cycleId, retention });
            if (retention) {
                props.clearRetention();
            }
        } finally {
            useOrderCyclesStore.getState().fetchServerData(true);
        }
    }
    
    function revealActualCycle() {
        const actualCycle = useOrderCyclesStore.getState().clientData.actualCycle;
        const cyclesStore = useCyclesStore.getState();
        const index = cyclesStore.items.map((p, i) => ({ cycleId: p.cycleId, index: i })).filter(p => p.cycleId >= actualCycle).map(p => p.index).at(0);
        if (index != null) {
            cyclesStore.setSelectedItem(cyclesStore.items[index]);
            scrollToSelectedItemSubject.next();
        }
    }

    return <>
        {orderCycleDialog.render()}
        <DialogTitle>{customerOrderDialogTitle(props.ordId, false, () => "Order cycles", customer)}</DialogTitle>
        {renderClose(props.close)}
        <DialogContent>
            <Grid container columnSpacing={2}>
                <Grid item xs={3}>{propTextField(useOrderCyclesStore)("product", "Product", { disabled: true })}</Grid>
                <Grid item xs={3}>{propTextField(useOrderCyclesStore)("project", "Project", { disabled: true })}</Grid>
                <Grid item xs={2}>{propBoolField(useOrderCyclesStore)("autoSkip", "Auto skip?", { disabled: true })}</Grid>
                <Grid item xs={2}>{propNumberField(useOrderCyclesStore)("startingCycle", null, "Starting cycle", { disabled: true })}</Grid>
                <Grid item xs={2}>{propNumberField(useOrderCyclesStore)("actualCycle", null, "Actual cycle", { disabled: true })}</Grid>
            </Grid>
            <Grid container columnSpacing={4} alignItems="stretch">
                <Grid item xs={7}>
                    <Typography variant="h6">Cycles</Typography>
                    {renderListStore(useCyclesStore, {
                        scrollToSelectedItem$: scrollToSelectedItemSubject,
                        onDoubleClick: () => {
                            if (canOpenDetail) {
                                orderCycleDialog.show();
                            }
                        },
                        header: listHeaderItems([
                            canOpenDetail && ["edit", () => <Button color="edit" title="Edit" disabled={selectedCycle == null} onClick={() => orderCycleDialog.show()}><Edit /></Button>],
                            canManage && ["stopAutoSkip", () => <Button title="Stop auto skip"
                                disabled={selectedCycle == null || selectedCycle.cycleId < actualCycle} onClick={stopAutoSkip}><CodeOff /></Button>],
                            canManage && ["autoSkip", () => <Button title="Auto skip"
                                disabled={selectedCycle == null || selectedCycle.cycleId < actualCycle} onClick={autoSkip}><Code /></Button>],
                            ["start", () => <Button title="Reveal actual cycle" onClick={revealActualCycle}><Start /></Button>],
                        ]),
                    })}
                </Grid>
                <Grid item container xs={5} direction="column">
                    <Grid item><Typography variant="h6">Articles{selectedCycle && ` of cycle ${selectedCycle.cycleId}`}</Typography></Grid>
                    <Grid item flexGrow={1}>{renderListStore(useArticlesStore, {
                        autoHeight: false,
                    })}</Grid>
                </Grid>
            </Grid>
        </DialogContent>
        <DialogActions>
            <Button onClick={props.close} color="negative" variant="outlined">Close</Button>
        </DialogActions>
    </>;
}
